import { Box, BoxProps, Flex, VStack } from '@chakra-ui/react'
import { ScrollToOptions, useVirtualizer } from '@tanstack/react-virtual'
import { ReactNode, useEffect, useRef } from 'react'
import { ScrollArea, ScrollbarOptions } from '~/components/shared/ScrollArea'
import useDebounceCallback from '~/hooks/useDebounceCallback'
import { Size } from '~/types/ThemeTypes'

import { LoadingSpinner } from './LoadingSpinner'

const END_REACHED_COOLDOWN_MS = 100
export interface ScrollToIndexParams {
  index: number
  options?: ScrollToOptions
}

type ScrollingListProps<TItemData> = BoxProps & {
  itemData: TItemData[]
  selectedItemIndex?: number
  scrollToIndex?: ScrollToIndexParams
  loadThresholdPercentage?: number
  onBuildItemContent: (
    itemIndex: number,
    data: TItemData,
    isSelected: boolean,
  ) => ReactNode
  onEndReached?: () => void
  scrollbarOptions?: ScrollbarOptions
  showLoaderFooter?: boolean
  itemSpacing?: string
}

const LoaderFooter = () => {
  return (
    <Flex justifyContent="center" alignItems="center" height="32px">
      <LoadingSpinner size={Size.sm} />
    </Flex>
  )
}

export const ScrollingList = <TItemData,>(
  props: ScrollingListProps<TItemData>,
) => {
  const {
    itemData,
    selectedItemIndex,
    scrollToIndex,
    loadThresholdPercentage = 0.1,
    onBuildItemContent,
    onEndReached,
    scrollbarOptions = { hidden: false, showOnHoverOnly: false },
    showLoaderFooter = true,
    itemSpacing,
    ...rest
  } = props

  const handleEndReached = useDebounceCallback(
    () => {
      if (!onEndReached) {
        return
      }

      onEndReached()
    },
    END_REACHED_COOLDOWN_MS,
    { leading: true, trailing: false },
  )

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: itemData.length,
    estimateSize: () => 100,
    getScrollElement: () => scrollAreaRef.current,
  })

  const virtualSize = virtualizer.getTotalSize()
  const virtualItems = virtualizer.getVirtualItems()
  const items = virtualItems
    .sort((a, b) => a.index - b.index)
    .map((virtualItem) => {
      const item = itemData[virtualItem.index]
      const start = virtualItem.start
      const isSelected = selectedItemIndex === virtualItem.index
      return (
        <Box
          key={virtualItem.key}
          data-index={virtualItem.index}
          position={'absolute'}
          transform={`translateY(${start}px)`}
          ref={virtualizer.measureElement}
          width="100%"
          height="fit-content"
          className="mango-list-item"
        >
          {onBuildItemContent(virtualItem.index, item as TItemData, isSelected)}
        </Box>
      )
    })

  useEffect(() => {
    const currentVirtualItems = virtualizer.getVirtualItems()
    const lastItem = currentVirtualItems.reverse()[0]
    if (!lastItem) {
      return
    }

    const loadThreshold = itemData.length * loadThresholdPercentage
    const isNearEnd = lastItem.index >= itemData.length - loadThreshold
    if (isNearEnd) {
      handleEndReached()
    }
  }, [virtualItems.length, itemData.length])

  useEffect(() => {
    if (!scrollToIndex || scrollToIndex.index < 0) {
      return
    }

    virtualizer.scrollToIndex(scrollToIndex.index, scrollToIndex.options)
  }, [scrollToIndex])

  return (
    <Flex overflow="auto" {...rest}>
      <Flex zIndex={0} width="100%" pr="0.25rem">
        <ScrollArea
          data-testid="scrolling-list"
          flex="1"
          width="100%"
          ref={scrollAreaRef}
          overflow="auto"
          position="relative"
          scrollbarOptions={scrollbarOptions}
        >
          <VStack align="stretch" spacing=".25rem" width="100%">
            <Box
              height={`${virtualSize}px`}
              __css={{
                '*.mango-list-item+*.mango-list-item': { mt: itemSpacing },
              }}
            >
              {items}
            </Box>
            {showLoaderFooter && <LoaderFooter />}
          </VStack>
        </ScrollArea>
      </Flex>
    </Flex>
  )
}
